import React from 'react';
import dayjs from 'dayjs';
import { PrintPageHeader, PrintFooter, printStyles } from './PrintBase';

const formatCurrency = (val) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(val || 0);

const KPIsCategorizasPrint = React.forwardRef(function KPIsCategorizasPrint({ data, valorTotal }, ref) {
  const categorias = data || [];

  const totalCategorias = categorias.length;
  const totalProductos = categorias.reduce((acc, cat) => acc + (cat.total_productos || 0), 0);
  const categoriasConStockBajo = categorias.filter((cat) => (cat.productos_bajo_stock || 0) > 0).length;
  const categoriasConSobreStock = categorias.filter((cat) => (cat.productos_sobre_stock || 0) > 0).length;

  const summaryTotals = {
    total_productos: categorias.reduce((s, c) => s + (c.total_productos || 0), 0),
    valor_inventario: categorias.reduce((s, c) => s + Number(c.valor_inventario || 0), 0),
    productos_bajo_stock: categorias.reduce((s, c) => s + Number(c.productos_bajo_stock || 0), 0),
    productos_sin_stock: categorias.reduce((s, c) => s + Number(c.productos_sin_stock || 0), 0),
    productos_sobre_stock: categorias.reduce((s, c) => s + Number(c.productos_sobre_stock || 0), 0),
    entradas_qty: categorias.reduce((s, c) => s + (c.entradas_qty || 0), 0),
    salidas_qty: categorias.reduce((s, c) => s + (c.salidas_qty || 0), 0),
    entradas_valor: categorias.reduce((s, c) => s + (c.entradas_valor || 0), 0),
    salidas_valor: categorias.reduce((s, c) => s + (c.salidas_valor || 0), 0),
    total_movimientos_mes: categorias.reduce((s, c) => s + (c.total_movimientos_mes || 0), 0),
  };

  return (
    <div ref={ref} style={printStyles.page}>
      <PrintPageHeader
        titulo="Reporte: KPIs por Categoría"
        subtitulo={`Datos del mes de ${dayjs().format('MMMM YYYY')}`}
      />

      {/* KPI Grid */}
      <div style={printStyles.kpiGrid}>
        <div style={printStyles.kpiCard}>
          <div style={printStyles.kpiLabel}>Total Categorías</div>
          <div style={{ ...printStyles.kpiValue, color: '#1677ff' }}>
            {totalCategorias}
          </div>
        </div>
        <div style={printStyles.kpiCard}>
          <div style={printStyles.kpiLabel}>Valor Total Inventario</div>
          <div style={{ ...printStyles.kpiValue, color: '#722ed1', fontSize: '12pt' }}>
            {formatCurrency(valorTotal)}
          </div>
        </div>
        <div style={printStyles.kpiCard}>
          <div style={printStyles.kpiLabel}>Total Productos Activos</div>
          <div style={{ ...printStyles.kpiValue, color: '#52c41a' }}>
            {totalProductos}
          </div>
        </div>
        <div style={printStyles.kpiCard}>
          <div style={printStyles.kpiLabel}>Categorías con Stock Bajo</div>
          <div style={{ ...printStyles.kpiValue, color: categoriasConStockBajo > 0 ? '#fa8c16' : '#52c41a' }}>
            {categoriasConStockBajo}
          </div>
        </div>
        <div style={printStyles.kpiCard}>
          <div style={printStyles.kpiLabel}>Categorías con Sobre Stock</div>
          <div style={{ ...printStyles.kpiValue, color: categoriasConSobreStock > 0 ? '#722ed1' : '#52c41a' }}>
            {categoriasConSobreStock}
          </div>
        </div>
      </div>

      {/* Comparison summary table */}
      <div style={printStyles.sectionTitle}>Resumen Comparativo por Categoría</div>
      <table style={printStyles.table}>
        <thead>
          <tr>
            <th style={printStyles.th}>Categoría</th>
            <th style={{ ...printStyles.th, textAlign: 'center' }}>Productos</th>
            <th style={{ ...printStyles.th, textAlign: 'right' }}>Valor Inventario</th>
            <th style={{ ...printStyles.th, textAlign: 'center' }}>% del Total</th>
            <th style={{ ...printStyles.th, textAlign: 'center' }}>Bajo Stock</th>
            <th style={{ ...printStyles.th, textAlign: 'center' }}>Sin Stock</th>
            <th style={{ ...printStyles.th, textAlign: 'center' }}>Sobre Stock</th>
            <th style={{ ...printStyles.th, textAlign: 'right' }}>Entradas Mes (uds.)</th>
            <th style={{ ...printStyles.th, textAlign: 'right' }}>Salidas Mes (uds.)</th>
            <th style={{ ...printStyles.th, textAlign: 'right' }}>Valor Entradas</th>
            <th style={{ ...printStyles.th, textAlign: 'right' }}>Valor Salidas</th>
            <th style={{ ...printStyles.th, textAlign: 'center' }}>Mov. Mes</th>
          </tr>
        </thead>
        <tbody>
          {categorias.map((cat, index) => (
            <tr key={cat.id} style={index % 2 === 0 ? {} : { backgroundColor: '#fafafa' }}>
              <td style={{ ...printStyles.td, fontWeight: 'bold' }}>{cat.nombre || '—'}</td>
              <td style={{ ...printStyles.tdCenter, color: '#1677ff', fontWeight: 'bold' }}>
                {cat.total_productos || 0}
              </td>
              <td style={{ ...printStyles.tdRight, color: '#722ed1', fontWeight: 'bold' }}>
                {formatCurrency(cat.valor_inventario)}
              </td>
              <td style={printStyles.tdCenter}>
                {parseFloat(cat.porcentaje_valor || 0).toLocaleString('es-MX')}%
              </td>
              <td style={{
                ...printStyles.tdCenter,
                color: (cat.productos_bajo_stock || 0) > 0 ? '#d46b08' : '#389e0d',
                fontWeight: 'bold'
              }}>
                {cat.productos_bajo_stock || 0}
              </td>
              <td style={{
                ...printStyles.tdCenter,
                color: (cat.productos_sin_stock || 0) > 0 ? '#cf1322' : '#389e0d',
                fontWeight: 'bold'
              }}>
                {cat.productos_sin_stock || 0}
              </td>
              <td style={{
                ...printStyles.tdCenter,
                color: (cat.productos_sobre_stock || 0) > 0 ? '#531dab' : '#389e0d',
                fontWeight: 'bold'
              }}>
                {cat.productos_sobre_stock || 0}
              </td>
              <td style={{ ...printStyles.tdRight, color: '#389e0d' }}>
                {parseFloat(cat.total_entradas || 0).toLocaleString('es-MX')}
              </td>
              <td style={{ ...printStyles.tdRight, color: '#cf1322' }}>
                {parseFloat(cat.total_salidas || 0).toLocaleString('es-MX')}
              </td>
              <td style={{ ...printStyles.tdRight, color: '#389e0d' }}>
                {formatCurrency(cat.entradas_valor)}
              </td>
              <td style={{ ...printStyles.tdRight, color: '#cf1322' }}>
                {formatCurrency(cat.salidas_valor)}
              </td>
              <td style={{ ...printStyles.tdCenter, color: '#1677ff' }}>
                {cat.total_movimientos_mes || 0}
              </td>
            </tr>
          ))}
          {categorias.length === 0 && (
            <tr>
              <td colSpan={12} style={{ ...printStyles.tdCenter, color: '#888', fontStyle: 'italic' }}>
                Sin datos de categorías
              </td>
            </tr>
          )}
        </tbody>
        {categorias.length > 0 && (
          <tfoot>
            <tr style={printStyles.trSummary}>
              <td style={{ ...printStyles.td, fontWeight: 'bold' }}>TOTALES</td>
              <td style={{ ...printStyles.tdCenter, fontWeight: 'bold', color: '#1677ff' }}>
                {summaryTotals.total_productos}
              </td>
              <td style={{ ...printStyles.tdRight, fontWeight: 'bold', color: '#722ed1' }}>
                {formatCurrency(summaryTotals.valor_inventario)}
              </td>
              <td style={{ ...printStyles.tdCenter, fontWeight: 'bold' }}>100%</td>
              <td style={{
                ...printStyles.tdCenter, fontWeight: 'bold',
                color: summaryTotals.productos_bajo_stock > 0 ? '#d46b08' : '#389e0d'
              }}>
                {summaryTotals.productos_bajo_stock}
              </td>
              <td style={{
                ...printStyles.tdCenter, fontWeight: 'bold',
                color: summaryTotals.productos_sin_stock > 0 ? '#cf1322' : '#389e0d'
              }}>
                {summaryTotals.productos_sin_stock}
              </td>
              <td style={{
                ...printStyles.tdCenter, fontWeight: 'bold',
                color: summaryTotals.productos_sobre_stock > 0 ? '#531dab' : '#389e0d'
              }}>
                {summaryTotals.productos_sobre_stock}
              </td>
              <td style={{ ...printStyles.tdRight, fontWeight: 'bold', color: '#389e0d' }}>
                {parseFloat(summaryTotals.entradas_qty).toLocaleString('es-MX')}
              </td>
              <td style={{ ...printStyles.tdRight, fontWeight: 'bold', color: '#cf1322' }}>
                {parseFloat(summaryTotals.salidas_qty).toLocaleString('es-MX')}
              </td>
              <td style={{ ...printStyles.tdRight, fontWeight: 'bold', color: '#389e0d' }}>
                {formatCurrency(summaryTotals.entradas_valor)}
              </td>
              <td style={{ ...printStyles.tdRight, fontWeight: 'bold', color: '#cf1322' }}>
                {formatCurrency(summaryTotals.salidas_valor)}
              </td>
              <td style={{ ...printStyles.tdCenter, fontWeight: 'bold', color: '#1677ff' }}>
                {summaryTotals.total_movimientos_mes}
              </td>
            </tr>
          </tfoot>
        )}
      </table>

      {/* Per-category product tables */}
      {categorias
        .filter((cat) => cat.productos && cat.productos.length > 0)
        .map((cat) => (
          <div key={cat.id} style={{ pageBreakBefore: 'auto', marginTop: '16pt' }}>
            <div style={printStyles.sectionTitle}>
              Productos — {cat.nombre}
              <span style={{ fontSize: '9pt', fontWeight: 'normal', color: '#555', marginLeft: '8pt' }}>
                ({cat.productos.length} producto{cat.productos.length !== 1 ? 's' : ''})
              </span>
            </div>
            <table style={printStyles.table}>
              <thead>
                <tr>
                  <th style={printStyles.th}>Clave</th>
                  <th style={printStyles.th}>Nombre</th>
                  <th style={{ ...printStyles.th, textAlign: 'right' }}>Stock Actual</th>
                  <th style={{ ...printStyles.th, textAlign: 'right' }}>Stock Mínimo</th>
                  <th style={{ ...printStyles.th, textAlign: 'right' }}>Stock Máximo</th>
                  <th style={{ ...printStyles.th, textAlign: 'right' }}>Precio Compra</th>
                  <th style={{ ...printStyles.th, textAlign: 'right' }}>Precio Venta</th>
                  <th style={{ ...printStyles.th, textAlign: 'center' }}>Unidad</th>
                </tr>
              </thead>
              <tbody>
                {cat.productos.map((prod, index) => {
                  const stockActual = parseFloat(prod.stock_actual || 0);
                  const stockMinimo = parseFloat(prod.stock_minimo || 0);
                  const stockMaximo = parseFloat(prod.stock_maximo || 0);
                  let stockColor = '#389e0d';
                  if (stockActual === 0) stockColor = '#cf1322';
                  else if (stockActual <= stockMinimo) stockColor = '#d46b08';
                  else if (stockMaximo > 0 && stockActual >= stockMaximo) stockColor = '#531dab';

                  return (
                    <tr key={prod.id} style={index % 2 === 0 ? {} : { backgroundColor: '#fafafa' }}>
                      <td style={printStyles.td}>{prod.clave || '—'}</td>
                      <td style={printStyles.td}>{prod.nombre || '—'}</td>
                      <td style={{ ...printStyles.tdRight, color: stockColor, fontWeight: 'bold' }}>
                        {stockActual.toLocaleString('es-MX')} {prod.unidad_medida || ''}
                      </td>
                      <td style={printStyles.tdRight}>
                        {stockMinimo.toLocaleString('es-MX')} {prod.unidad_medida || ''}
                      </td>
                      <td style={printStyles.tdRight}>
                        {stockMaximo > 0
                          ? `${stockMaximo.toLocaleString('es-MX')} ${prod.unidad_medida || ''}`
                          : '—'}
                      </td>
                      <td style={printStyles.tdRight}>
                        {formatCurrency(prod.precio_compra)}
                      </td>
                      <td style={printStyles.tdRight}>
                        {formatCurrency(prod.precio_venta)}
                      </td>
                      <td style={printStyles.tdCenter}>{prod.unidad_medida || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}

      <PrintFooter />
    </div>
  );
});

export default KPIsCategorizasPrint;
